import { Injectable, inject, Signal, effect, RESPONSE_INIT } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, ObservableInput, ObservedValueOf, OperatorFunction, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import { RoutePaths } from '../../../routes-constants';

export enum ErrorType {
  BadRequest = 'bad-request',
  Forbidden = 'forbidden',
  NotFound = 'not-found',
  ServerError = 'server-error',
}

const NAVIGATION_OPTIONS = {
  skipLocationChange: true,
};

export function handleResponseError<T, O extends ObservableInput<J>, J>(
  router: Router,
): OperatorFunction<T, T | ObservedValueOf<O>> {
  return (source) =>
    source.pipe(
      catchError((e: unknown) => {
        if (e instanceof HttpErrorResponse) {
          switch (e.status) {
            case 400:
              router.navigate(
                ['/', RoutePaths.ErrorRoute.root, RoutePaths.ErrorRoute.badRequest],
                NAVIGATION_OPTIONS,
              );
              break;
            case 403:
              router.navigate(
                ['/', RoutePaths.ErrorRoute.root, RoutePaths.ErrorRoute.forbidden],
                NAVIGATION_OPTIONS,
              );
              break;
            case 404:
              router.navigate(
                ['/', RoutePaths.ErrorRoute.root, RoutePaths.ErrorRoute.notFound],
                NAVIGATION_OPTIONS,
              );
              break;
            case 500:
            default:
              router.navigate(
                ['/', RoutePaths.ErrorRoute.root, RoutePaths.ErrorRoute.serverError],
                NAVIGATION_OPTIONS,
              );
              break;
          }

          return EMPTY;
        }

        router.navigate(
          ['/', RoutePaths.ErrorRoute.root, RoutePaths.ErrorRoute.serverError],
          NAVIGATION_OPTIONS,
        );

        return throwError(() => e);
      }),
    );
}

export function handleServerResponse<T>(router: Router): OperatorFunction<T, T> {
  return (source) => {
    return source.pipe(
      tap((data) => {
        if (!data) {
          router.navigate(
            ['/', RoutePaths.ErrorRoute.root, RoutePaths.ErrorRoute.notFound],
            NAVIGATION_OPTIONS,
          );
        }
      }),
      handleResponseError<T, ObservableInput<T>, T>(router),
    );
  };
}

export function handleNotFound(hasData: Signal<boolean>, router: Router): void {
  effect(() => {
    if (hasData()) {
      return;
    }

    router.navigate(
      ['/', RoutePaths.ErrorRoute.root, RoutePaths.ErrorRoute.notFound],
      NAVIGATION_OPTIONS,
    );
  });
}

@Injectable()
export class ErrorResponseService {
  private readonly res = inject(RESPONSE_INIT, { optional: true });

  setErrorCode(type: ErrorType): void {
    if (!this.res) {
      return;
    }

    if (type === undefined) {
      this.res.status = 200;

      return;
    }

    let code;

    switch (type) {
      case ErrorType.BadRequest:
        code = 400;
        break;
      case ErrorType.Forbidden:
        code = 403;
        break;
      case ErrorType.ServerError:
        code = 500;
        break;

      case ErrorType.NotFound:
      default:
        code = 404;
    }

    this.res.status = code;
  }
}
